import {Field, Filter, RefField, SqlOperationInfo} from "../../../Types";
import {useAppDispatch, useAppSelector} from "../../../hooks";
import {LANG} from "../../CONSTANTS";
import {useMemo} from "react";
import styled from "styled-components";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {faMaxcdn, faSearchengin} from "@fortawesome/free-brands-svg-icons";
import {fetchTableByQuery, setActiveTable, setTableFilters} from "../../../features/tableSlice";
import {nanoid} from "@reduxjs/toolkit";
import {fa1, faArrowDownShortWide, faArrowDownWideShort, faEye} from "@fortawesome/free-solid-svg-icons";
import {selectRefData} from "./RefValueUI";
import {selectActiveTable, selectProjectSchema} from "../../../features/projectSlice";


export default function RefConditionValue({filters, fieldSchema, refKey}: {
    filters: Filter[],
    fieldSchema: Field & { ref: RefField },
    refKey: string
}) {
    const project = useAppSelector(selectProjectSchema);
    const table = useAppSelector(selectActiveTable);
    const data = useAppSelector(selectRefData(fieldSchema.ref.entity, refKey));

    const refEntity = project.refEntities![fieldSchema.ref.entity];

    const dispatch = useAppDispatch();

    function searchRef(fields: Record<string, Field>, foreignFields: Record<string, Field>) {
        dispatch(setActiveTable(refEntity.table))
        dispatch(setTableFilters(filters.map(f => ({
            ...f,
            locked: true,
            nanoid: nanoid(),
            type: fields[f.field]?.type || foreignFields[f.field].type
        }))))
        dispatch(fetchTableByQuery({clean: true, pageIndex: 1}))
    }

    const {str, fields, foreignFields} = useMemo(() => {
        const fields = Object.fromEntries(project.tables[table].fields.map(f => ([f.key, f])));
        const foreignFields = Object.fromEntries(project.tables[refEntity.table].fields.map(f => ([f.key, f])));
        return {
            str: filters.map(f => `[${fields[f.field]?.title[LANG] || foreignFields[f.field]?.title[LANG]}] ${SqlOperationInfo[f.filterOp][LANG]} [${getRefConditionValue(fields[f.field] || foreignFields[f.field], f.value)}]`).join("; "),
            fields: fields,
            foreignFields: foreignFields
        }
    }, [filters, project, refEntity.table, table]);


    return <Container>
        <button className={'btn-cmn'} onClick={() => searchRef(fields, foreignFields)}>
            <FontAwesomeIcon icon={faSearchengin} title={"Шукати"} className={'fa-color'} style={{width: "1.2rem", height: "1.2rem"}}/>
        </button>
        <span className={'text-pad'}>{str}</span>
        {!fieldSchema.ref.single && <div className={'ic-center'}>
            <FontAwesomeIcon icon={(data && data.inCondition > 0) ? faEye : faMaxcdn} title={(data && data.inCondition > 0) ? "Показано" : "Показати максимум"} className={'fa-color'}
                             style={{width: "1.2rem", height: "1.2rem"}}/>
            <span
                className={'text-pad'}>{(data && data.inCondition > 0) ? Math.min(fieldSchema.ref.limit, data.inCondition) + "/" + data.inCondition : fieldSchema.ref.limit}</span>
        </div>}
        {fieldSchema.ref.single && <div className={'ic-center'}>
            <FontAwesomeIcon icon={fa1} title={"Унікальний"} className={'fa-color'} style={{width: "1.2rem", height: "1.2rem"}}/>
        </div>}
        <div className={'ic-center'}>
            <FontAwesomeIcon icon={fieldSchema.ref.order.way === "ASC" ? faArrowDownShortWide : faArrowDownWideShort}
                             title={"Сортувати"}
                             className={'fa-color'} style={{width: "1.2rem", height: "1.2rem"}}/>
            <span className={'text-pad'}>{fieldSchema.ref?.order.field}</span>
        </div>
    </Container>
}

export function getRefConditionValue(field: Field, value: any) {
    if (value === '')
        return <i>*порожній рядок*</i>

    if (Array.isArray(value) && value.length === 0)
        return <i>*відсутні опції*</i>

    if (!field)
        return value
    if (field.type === "map" && field.map) {
        if (Array.isArray(value))
            return (value as string[]).map(v => field.map?.values?.[v]?.lang[LANG] || v).join(", ")
    }

    return String(value)
}

const Container = styled.div`
    display: inline-flex;
    align-items: start;
    gap: .5rem;
    font-size: .85rem;

    & .text-pad {
        padding-top: 2px;
    }

    & .ic-center {
        display: flex;
        gap: .2rem;
        align-items: center;
    }

    & button {
        padding: 0;
        border: none;
        background: none;
        rotate: y 180deg;
    }
`
